import { defineStore } from 'pinia';
import {
  queryCustomFormFields,
  type CustomForm,
  type CustomFormField,
  type CustomFormFieldUpsertPayload,
  type CustomFormUpsertPayload,
} from '~/composables/customForms/types';
import { useMutation, useQuery } from '@tanstack/vue-query';
import {
  Configuration,
  FundingSourceAlert,
  FundingSourceWarning,
  LocationInterface,
  RequiredDocument,
  ResponseData,
} from '~/composables/types/RequiredDocument';
import { globalFetchOptions } from '~/utils/helpers';
import { SavePersonalDetails } from '~/composables/types/PersonalDetails';
import { FilterOptions, Pagination, Sort } from '~/composables/types/Common';

export const useAdminStore = defineStore(
  'admin-store',
  () => {
    const getDocuments = () => {
      return $fetch<ResponseData<RequiredDocument>>(`/admin/required-documents`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    const getUserInfo = (userId: number) => {
      return $fetch<ResponseData<RequiredDocument>>(`/admin/user-profile/${userId}`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    const createDocument = () => {
      return useMutation((data: any) =>
        $fetch<{ success: boolean }>(`/admin/required-documents`, {
          ...globalFetchOptions(),
          method: 'POST',
          retry: false,
          body: data,
        })
      );
    };

    const updateDocument = (docId: string) => {
      return useMutation((data: any) =>
        $fetch<{ success: boolean }>(`/admin/required-documents/${docId}`, {
          ...globalFetchOptions(),
          method: 'PUT',
          retry: false,
          body: data,
        })
      );
    };

    const deleteDocument = (options: any) => {
      return useMutation(
        (data: any) =>
          $fetch<{ success: boolean }>(`/admin/required-documents/${data['docId']}`, {
            ...globalFetchOptions(),
            method: 'DELETE',
            retry: false,
          }),
        options
      );
    };

    const getConfigurations = () => {
      return $fetch<ResponseData<Configuration>>(`/admin/approval-notification/configurations`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };
    const createNotification = () => {
      return useMutation((data: any) =>
        $fetch<{ success: boolean }>(`/admin/approval-notification/configurations`, {
          ...globalFetchOptions(),
          method: 'POST',
          retry: false,
          body: data,
        })
      );
    };
    const updateConfiguration = (confId: string) => {
      return useMutation((data: any) =>
        $fetch<{ success: boolean }>(`/admin/approval-notification/configurations/${confId}`, {
          ...globalFetchOptions(),
          method: 'PUT',
          retry: false,
          body: data,
        })
      );
    };

    const deleteConfiguration = (options: any) => {
      return useMutation(
        (data: any) =>
          $fetch<{ success: boolean }>(`/admin/approval-notification/configurations/${data['confId']}`, {
            ...globalFetchOptions(),
            method: 'DELETE',
            retry: false,
          }),
        options
      );
    };

    const getLocations = (all?: boolean, locIds?: string) => {
      if (all === undefined) all = false;
      return $fetch<ResponseData<LocationInterface[]>>(`/admin/locations?allLocations=${all}&locIds=${locIds ?? ''}`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    const getConfigurationById = (id: string) => {
      return $fetch<ResponseData<Configuration>>(`/admin/approval-notification/configurations/${id}`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    /** Friendly name of a Yooz organizational unit; 404 when the code does not exist */
    const getYoozOrgUnitName = (orgUnitCode: string) => {
      return $fetch<{ success: boolean; data: string }>(`/admin/yooz/org-units/${encodeURIComponent(orgUnitCode)}`, {
        ...globalFetchOptions(),
        method: 'get',
        retry: false,
      });
    };

    const getFundingSourceById = (id: string) => {
      return $fetch<{ success: boolean; data: FundingSourceWarning }>(`/admin/funding-source-warning/configurations/${id}`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    const createFundingSourceConf = () => {
      return useMutation((data: any) =>
        $fetch<{ success: boolean }>(`/admin/funding-source-warning/configurations`, {
          ...globalFetchOptions(),
          method: 'POST',
          retry: false,
          body: data,
        })
      );
    };
    const updateFundingSourceConf = (confId: string) => {
      return useMutation((data: any) =>
        $fetch<{ success: boolean }>(`/admin/funding-source-warning/configurations/${confId}`, {
          ...globalFetchOptions(),
          method: 'PUT',
          retry: false,
          body: data,
        })
      );
    };

    const deleteFundingSourceConf = (options: any) => {
      return useMutation(
        (data: any) =>
          $fetch<{ success: boolean }>(`/admin/funding-source-warning/configurations/${data['confId']}`, {
            ...globalFetchOptions(),
            method: 'DELETE',
            retry: false,
          }),
        options
      );
    };
    const getFundingSourceConf = () => {
      return $fetch<ResponseData<FundingSourceWarning>>(`/admin/funding-source-warning/configurations`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    const updateFundingSourceEnabled = () => {
      return useMutation((data: any) =>
        $fetch<{ success: boolean }>(`/admin/funding-source-warning/configurations/${data.id}`, {
          ...globalFetchOptions(),
          method: 'PUT',
          retry: false,
          body: data,
        })
      );
    };

    const notifChecked = ref(false);
    const setNotifChecked = (val: boolean) => {
      notifChecked.value = val;
    };
    const getFundingSourceNotification = () => {
      notifChecked.value = true;
      return $fetch<ResponseData<FundingSourceAlert>>(`/funding-sources/funding-source-warning/alerts`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    const getApplicationAndIncomeDocuments = (options: any) => {
      const fetcher = async () =>
        $fetch<{ success: boolean; data: ApplicationOrIncomeDocument }>(`/admin/application-income-documents`, {
          ...globalFetchOptions(),
          method: 'get',
        });
      return useQuery({ queryKey: ['app-income-docs'], queryFn: fetcher, ...globalUseQueryOptions(), ...options });
    };

    const getApplicationFilters = (options: any, type?: string) => {
      const fetcher = async () =>
        $fetch<{ success: boolean; data: ApplicationOrIncomeDocument }>(
          `/admin/default-filters${type ? `?filterType=${type}` : ''}`,
          {
            ...globalFetchOptions(),
            method: 'get',
          }
        );
      return useQuery({
        queryKey: ['admin-application-default-filter', type ?? 'normal'],
        queryFn: fetcher,
        ...globalUseQueryOptions(),
        ...options,
      });
    };
    const getApplicationFiltersQuery = (type: string | null) => {
      return () =>
        $fetch<{ success: boolean; data: ApplicationOrIncomeDocument }>(
          `/admin/default-filters${type ? `?filterType=${type}` : ''}`,
          {
            ...globalFetchOptions(),
            method: 'get',
          }
        );
    };
    const saveApplicationFilter = (type: string | null) => {
      return useMutation((data: any) =>
        $fetch<{ success: boolean }>(`/admin/default-filters`, {
          ...globalFetchOptions(),
          method: 'POST',
          retry: false,
          body: {
            ...data,
            filterType: type,
          },
        })
      );
    };

    const deleteFilter = () => {
      return useMutation((data: { id: string }) =>
        $fetch<{ success: boolean }>(`/admin/default-filters/${data.id}`, {
          ...globalFetchOptions(),
          method: 'DELETE',
        })
      );
    };

    const reportOpen = ref(false);

    const getCustomField = (filter: FilterOptions, pagination: Pagination, sort: Sort) => {
      return $fetch<ResponseData<RequiredDocument>>(
        `/form-field-step/custom?${generateFilter(filter)}page=${pagination.currentPage}&pageSize=${
          pagination.perPage
        }&sort=${sort.id}&order=${sort.desc ? 'desc' : 'asc'}`,
        {
          ...globalFetchOptions(),
          method: 'get',
        }
      );
    };

    // Custom Forms (BRD Custom Forms) — /custom-forms
    const getCustomForms = (filter: FilterOptions, pagination: Pagination, sort: Sort) => {
      return $fetch<ResponseData<CustomForm>>(
        `/custom-forms?${generateFilter(filter)}page=${pagination.currentPage}&pageSize=${pagination.perPage}&sort=${
          sort.id
        }&order=${sort.desc ? 'desc' : 'asc'}`,
        {
          ...globalFetchOptions(),
          method: 'get',
        }
      );
    };

    const getCustomForm = (id: string) => {
      return $fetch<{ success: boolean; data: CustomForm }>(`/custom-forms/${id}`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    /**
     * Whether every service area / assistance type combination of the form requires an approval signature
     * (Service Area configuration) — else the combinations still to configure, as "Service Area / Assistance Type".
     */
    const getCustomFormApprovalSignaturesEnabled = (id: string) => {
      return $fetch<{
        success: boolean;
        data: { approvalSignaturesEnabled: boolean; serviceAreaInNeedOfConfiguration: string[] | null };
      }>(`/custom-forms/${id}/approval-signatures-enabled`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    /** Generic blob upload (POST /files/upload) → { name, url: blobId } */
    const uploadFile = async (file: File) => {
      const body = new FormData();
      body.append('formFile', file, file.name);
      const response = await $fetch<{ success: boolean; data: { name: string; url: string } }>(`/files/upload`, {
        ...globalFetchOptions(),
        method: 'POST',
        body,
      });
      if (!response?.success || !response.data?.url) {
        throw new Error((response as any)?.error?.Message ?? (response as any)?.error?.message ?? 'Upload failed');
      }
      return { name: response.data.name, fileId: response.data.url };
    };

    /** Download a blob uploaded through uploadFile (no service-area check, unlike GET /files/{id}) */
    const downloadFile = (fileId: string) => {
      return $fetch<Blob>(`/files/download/${fileId}`, {
        ...globalFetchOptions(),
        method: 'GET',
        responseType: 'blob',
      });
    };

    /** Create / update a custom form */
    const saveCustomForm = () => {
      return useMutation((body: CustomFormUpsertPayload) =>
        $fetch<{ success: boolean; data: CustomForm }>(`/custom-forms/upsert`, {
          ...globalFetchOptions(),
          method: 'POST',
          body,
        })
      );
    };

    /**
     * Questions / HTML blocks of a custom form. The endpoint currently returns the whole list as a plain
     * array and ignores filter/sort/paging, so those are applied client-side when that shape comes back.
     */
    const getCustomFormFields = async (formId: string, filter: FilterOptions, pagination: Pagination, sort: Sort) => {
      const response = await $fetch<{ success: boolean; data: any }>(
        `/custom-forms/${formId}/fields?${generateFilter(filter)}page=${pagination.currentPage}&pageSize=${
          pagination.perPage
        }&sort=${sort.id}&order=${sort.desc ? 'desc' : 'asc'}`,
        {
          ...globalFetchOptions(),
          method: 'get',
        }
      );
      if (Array.isArray(response?.data)) {
        return queryCustomFormFields(response.data as CustomFormField[], filter, pagination, sort);
      }
      return response as ResponseData<CustomFormField>;
    };

    /** Persist the display order of a custom form's questions: [{ id, displayOrder }] */
    const saveCustomFormFieldsOrder = (formId: string) => {
      return useMutation((data: { id: string; displayOrder: number; indent?: number }[]) =>
        $fetch<{ success: boolean }>(`/custom-forms/${formId}/update-display-order`, {
          ...globalFetchOptions(),
          method: 'PUT',
          body: data,
        })
      );
    };

    /** Create / update a question or HTML block of a custom form */
    const saveCustomFormQuestion = (formId: string) => {
      return useMutation((body: CustomFormFieldUpsertPayload) =>
        $fetch<{ success: boolean; data: any }>(`/custom-forms/${formId}/upsert`, {
          ...globalFetchOptions(),
          method: 'POST',
          body,
        })
      );
    };

    const getCustomFormHistory = (formId: string) => {
      return $fetch<{ success: boolean; data: any[] }>(`/custom-forms/${formId}/history`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    const getCustomFormComments = (formId: string) => {
      return $fetch<{ success: boolean; data: { result: any[] } }>(`/custom-forms/${formId}/comments`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    const addCustomFormComment = (formId: string) => {
      return useMutation((body: { message: string }) =>
        $fetch<{ success: boolean; data: string }>(`/custom-forms/${formId}/comments`, {
          ...globalFetchOptions(),
          method: 'POST',
          body,
        })
      );
    };

    const getCustomNotifications = (filter: FilterOptions, pagination: Pagination, sort: Sort) => {
      return $fetch<ResponseData<RequiredDocument>>(
        `/custom-notifications?${generateFilter(filter)}page=${pagination.currentPage}&pageSize=${pagination.perPage}&sort=${
          sort.id
        }&order=${sort.desc ? 'desc' : 'asc'}`,
        {
          ...globalFetchOptions(),
          method: 'get',
        }
      );
    };

    const getCustomWeblinks = (filter: FilterOptions, pagination: Pagination, sort: Sort) => {
      return $fetch<ResponseData<RequiredDocument>>(
        `/vendor-web-links?${generateFilter(filter)}page=${pagination.currentPage}&pageSize=${pagination.perPage}&sort=${
          sort.id
        }&order=${sort.desc ? 'desc' : 'asc'}`,
        {
          ...globalFetchOptions(),
          method: 'get',
        }
      );
    };

    const atHasFundingSource = (body: any) => {
      return $fetch<ResponseData<RequiredDocument>>(`/form-field-step/assistance-type/funding-sources`, {
        ...globalFetchOptions(),
        method: 'post',
        body,
      });
    };

    const getById = (id: string) => {
      return $fetch<ResponseData<RequiredDocument>>(`/form-field-step/custom/${id}`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    const getNotificationById = (id: string) => {
      return $fetch<ResponseData<RequiredDocument>>(`/custom-notifications/${id}`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    const notificationUploadFile = (id: string, body: any) => {
      return $fetch<ResponseData<RequiredDocument>>(`/custom-notifications/${id}/file`, {
        ...globalFetchOptions(),
        method: 'post',
        body,
      });
    };

    const notificationDeleteFile = (id: string) => {
      return $fetch<ResponseData<RequiredDocument>>(`/custom-notifications/${id}/file`, {
        ...globalFetchOptions(),
        method: 'DELETE',
      });
    };

    const saveCustomField = () => {
      return useMutation((data: any) =>
        $fetch<{ success: boolean }>(`/form-field-step/custom/upsert`, {
          ...globalFetchOptions(),
          method: 'POST',
          body: data,
        })
      );
    };

    const saveCustomNotification = (id: string | undefined) => {
      const link = id && id !== '' ? `/custom-notifications/${id}` : '/custom-notifications/';
      const method = id && id !== '' ? 'PUT' : 'POST';
      return useMutation((body: any) =>
        $fetch<{ success: boolean }>(link, {
          ...globalFetchOptions(),
          method,
          body,
        })
      );
    };

    const previewCustomNotification = (id: string | undefined) => {
      const link = `/custom-notifications/preview-message`;
      const method = 'POST';
      return useMutation((body: any) =>
        $fetch<{ success: boolean }>(link, {
          ...globalFetchOptions(),
          method,
          body,
        })
      );
    };

    const saveCustomWeblinks = () => {
      return useMutation((data: any) =>
        $fetch<{ success: boolean }>(`/vendor-web-links${data?.id ? '/' + data?.id : ''}`, {
          ...globalFetchOptions(),
          method: data?.id ? 'PUT' : 'POST',
          body: data,
        })
      );
    };

    const getWeblinksById = (id: string) => {
      return $fetch<ResponseData<RequiredDocument>>(`/vendor-web-links/${id}`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    const saveOrder = () => {
      return useMutation((data: any) =>
        $fetch<{ success: boolean }>(`/form-field-step/custom/update-display-order`, {
          ...globalFetchOptions(),
          method: 'PUT',
          body: data,
        })
      );
    };

    const refetchComments = ref(false);
    const setRefetchComments = () => {
      refetchComments.value = !refetchComments.value;
    };
    const getHistory = (assistanceTypeId: string) => {
      return $fetch<{ success: boolean; data: any }>(`/form-field-step/history`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };
    const getHistoryNotification = (id: string) => {
      return $fetch<{ success: boolean; data: any }>(`/custom-notifications/${id}/history`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    const getComments = () => {
      return $fetch<{ success: boolean; data: unknown }>(`/form-field-step/comments`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    const getCommentsNotification = (id: string) => {
      return $fetch<{ success: boolean; data: unknown }>(`/custom-notifications/${id}/comments`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    const addComment = () => {
      return useMutation((formData: { message: string }) => {
        return $fetch<SavePersonalDetails>(`/form-field-step/comments`, {
          ...globalFetchOptions(),
          method: 'POST',
          body: formData,
          retry: false,
        });
      });
    };

    const addCommentNotification = (id: string) => {
      return useMutation((formData: { message: string }) => {
        return $fetch<SavePersonalDetails>(`/custom-notifications/${id}/comments`, {
          ...globalFetchOptions(),
          method: 'POST',
          body: formData,
          retry: false,
        });
      });
    };

    const getWeblinksHistory = () => {
      return $fetch<{ success: boolean; data: any }>(`/vendor-web-links/history`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    const getWeblinksComments = () => {
      return $fetch<{ success: boolean; data: unknown }>(`/vendor-web-links/comments`, {
        ...globalFetchOptions(),
        method: 'get',
      });
    };

    const addWeblinksComment = () => {
      return useMutation((formData: { message: string }) => {
        return $fetch<SavePersonalDetails>(`/vendor-web-links/comments`, {
          ...globalFetchOptions(),
          method: 'POST',
          body: formData,
          retry: false,
        });
      });
    };
    return {
      getDocuments,
      createDocument,
      updateDocument,
      deleteDocument,
      createNotification,
      getConfigurations,
      updateConfiguration,
      deleteConfiguration,
      getLocations,
      getConfigurationById,
      getYoozOrgUnitName,
      getFundingSourceById,
      getFundingSourceConf,
      deleteFundingSourceConf,
      updateFundingSourceConf,
      createFundingSourceConf,
      updateFundingSourceEnabled,
      getFundingSourceNotification,
      notifChecked,
      setNotifChecked,
      getApplicationAndIncomeDocuments,
      getApplicationFilters,
      saveApplicationFilter,
      reportOpen,
      deleteFilter,
      getCustomField,
      getCustomForms,
      getCustomForm,
      getCustomFormApprovalSignaturesEnabled,
      saveCustomForm,
      uploadFile,
      downloadFile,
      saveCustomFormQuestion,
      getCustomFormFields,
      saveCustomFormFieldsOrder,
      getCustomFormHistory,
      getCustomFormComments,
      addCustomFormComment,
      saveCustomField,
      atHasFundingSource,
      getById,
      refetchComments,
      setRefetchComments,
      getHistory,
      getComments,
      addComment,
      getApplicationFiltersQuery,
      saveOrder,
      getCustomWeblinks,
      saveCustomWeblinks,
      getWeblinksById,
      getWeblinksHistory,
      getWeblinksComments,
      addWeblinksComment,
      getUserInfo,
      getCustomNotifications,
      saveCustomNotification,
      getNotificationById,
      notificationUploadFile,
      previewCustomNotification,
      notificationDeleteFile,
      getHistoryNotification,
      getCommentsNotification,
      addCommentNotification,
    };
  },
  {
    persist: {
      storage: sessionStorage,
    },
  }
);
